import { useState, useEffect, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, ToggleLeft, ToggleRight, Eye, EyeOff, Search, Loader2, Trophy, Plus, Trash2 } from "lucide-react";
import SponsorCreateDialog from "./SponsorCreateDialog";

/**
 * Pannello Admin Sponsor — vista compact con toggle di visibilità per ogni template.
 * Tutti i 20 template piattaforma sono disponibili di default nei caroselli manager.
 * L'admin può nascondere singoli sponsor con il toggle "Non mostrare".
 */
export default function LeagueSponsorsAdmin({ leagueId }) {
  const [settings, setSettings] = useState({ bonus_enabled: true, paid_enabled: true });
  const [templates, setTemplates] = useState([]);
  const [customSponsors, setCustomSponsors] = useState([]);
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, all, hid] = await Promise.all([
        api.get(`/leagues/${leagueId}/sponsors/settings`),
        api.get(`/leagues/${leagueId}/sponsors`),
        api.get(`/leagues/${leagueId}/sponsors/hidden`),
      ]);
      setSettings(s.data);
      setTemplates((all.data || []).filter(x => x.is_platform));
      // Sponsor custom: quelli della lega NON is_platform, che hanno category (bonus/paid)
      // e NON sono stati auto-clonati (cloned_from_platform_id assente).
      setCustomSponsors((all.data || []).filter(x =>
        !x.is_platform && !x.is_virtual && !x.cloned_from_platform_id && x.category && x.active !== false
      ));
      setHiddenIds(new Set(hid.data?.hidden_ids || []));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [leagueId]);

  const toggleSection = async (key) => {
    try {
      const r = await api.patch(`/leagues/${leagueId}/sponsors/settings`, { [key]: !settings[key] });
      setSettings(r.data);
      toast.success("Impostazione aggiornata");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleHidden = async (tpl) => {
    const willHide = !hiddenIds.has(tpl.sponsor_id);
    setBusy(b => ({ ...b, [tpl.sponsor_id]: true }));
    try {
      const r = await api.patch(`/leagues/${leagueId}/sponsors/hidden`, {
        template_id: tpl.sponsor_id, hidden: willHide,
      });
      setHiddenIds(new Set(r.data.hidden_ids));
      toast.success(willHide ? `${tpl.name} nascosto` : `${tpl.name} visibile`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(b => ({ ...b, [tpl.sponsor_id]: false })); }
  };

  const removeCustom = async (sp) => {
    if (!window.confirm(`Eliminare "${sp.name}"? Le sponsorship già assegnate resteranno attive ma lo sponsor sparirà dal carosello.`)) return;
    try {
      await api.delete(`/leagues/${leagueId}/sponsors/${sp.sponsor_id}`);
      toast.success(`Sponsor "${sp.name}" eliminato`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeLogo = (sp) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Max 3MB"); return; }
    setBusy(b => ({ ...b, [`logo_${sp.sponsor_id}`]: true }));
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("scope", "sponsor");
      const up = await api.post("/uploads/logo", fd, { headers: { "Content-Type": "multipart/form-data" }});
      await api.patch(`/leagues/${leagueId}/sponsors/${sp.sponsor_id}`, { logo_url: up.data.url });
      toast.success(`Logo di "${sp.name}" aggiornato`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(b => ({ ...b, [`logo_${sp.sponsor_id}`]: false })); }
  };

  const filtered = useMemo(() => {
    if (!query) return templates;
    const q = query.toLowerCase();
    return templates.filter(t => t.name.toLowerCase().includes(q));
  }, [templates, query]);

  if (loading) return <div className="text-slate-400 text-sm">Caricamento sponsor…</div>;

  const visibleCount = templates.length - hiddenIds.size;

  return (
    <div className="space-y-5" data-testid="league-sponsors-admin">
      {/* Toggle sezioni carosello */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
        <h3 className="font-black uppercase tracking-[0.2em] text-sm mb-3 flex items-center gap-2">
          <Sparkles size={14} className="text-amber-400"/> Caroselli attivi
        </h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <ToggleRow label="Sponsor Bonus (dà crediti al team)" value={settings.bonus_enabled} onClick={() => toggleSection("bonus_enabled")}/>
          <ToggleRow label="Sponsor Pagato (costa al team)" value={settings.paid_enabled} onClick={() => toggleSection("paid_enabled")}/>
        </div>
      </div>

      {/* Sponsor CUSTOM della lega */}
      <div>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-black uppercase tracking-[0.2em] text-sm text-white flex items-center gap-2">
            <Sparkles size={14} className="text-emerald-400"/> Sponsor personalizzati della lega
            <span className="text-slate-500 font-normal normal-case tracking-normal">({customSponsors.length})</span>
          </h3>
          <button onClick={() => setCreateOpen(true)}
            data-testid="btn-create-sponsor"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest bg-gradient-to-br from-emerald-400 to-emerald-600 text-slate-900 hover:brightness-110 shadow-lg shadow-emerald-500/20">
            <Plus size={13}/> Nuovo sponsor
          </button>
        </div>
        {customSponsors.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-4 text-center text-slate-400 text-xs italic">
            Nessuno sponsor personalizzato. Clicca <span className="font-bold text-emerald-300">Nuovo sponsor</span> per aggiungerne uno unico alla tua lega (nome, obiettivo, premi, logo).
          </div>
        ) : (
          <div className="grid gap-2" data-testid="admin-custom-list">
            {customSponsors.map(sp => {
              const isBonus = sp.category === "bonus_initial";
              const accent = isBonus ? "#10b981" : "#f59e0b";
              return (
                <div key={sp.sponsor_id}
                  data-testid={`admin-custom-${sp.sponsor_id}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2">
                  {sp.logo_url ? (
                    <img src={sp.logo_url} alt={sp.name}
                      className="w-11 h-11 object-contain shrink-0"
                      style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.4))" }}/>
                  ) : (
                    <div className="w-11 h-11 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0 text-xs font-black text-slate-400">
                      {sp.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <label className="cursor-pointer inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:text-emerald-300 shrink-0" title="Cambia logo">
                    <input type="file" accept="image/*" onChange={changeLogo(sp)} className="hidden"
                      data-testid={`admin-change-logo-${sp.sponsor_id}`}/>
                    {busy[`logo_${sp.sponsor_id}`] ? <Loader2 className="animate-spin" size={11}/> : "↺"}
                  </label>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-black text-white truncate">{sp.name}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-widest border"
                        style={{ background: `${accent}22`, borderColor: `${accent}55`, color: accent }}>
                        {isBonus ? "Bonus" : "Pagato"}
                      </span>
                    </div>
                    {sp.tagline && <div className="text-[10px] italic text-slate-400 truncate">{sp.tagline}</div>}
                    {sp.objective_desc && <div className="text-[10px] text-slate-500 truncate mt-0.5">🎯 {sp.objective_desc}</div>}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono uppercase tracking-widest shrink-0 text-right">
                    Base<br/><span className="font-black text-sm" style={{ color: accent }}>{sp.prize_credits}c</span>
                  </div>
                  <button onClick={() => removeCustom(sp)}
                    data-testid={`admin-remove-custom-${sp.sponsor_id}`}
                    className="p-1.5 rounded-lg hover:bg-rose-500/20 text-rose-400 shrink-0">
                    <Trash2 size={14}/>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lista rapida sponsor con toggle visibilità */}
      <div>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-black uppercase tracking-[0.2em] text-sm text-white flex items-center gap-2">
            <Trophy size={14} className="text-emerald-400"/> Sponsor piattaforma
            <span className="text-slate-500 font-normal normal-case tracking-normal">
              ({visibleCount} visibili · {hiddenIds.size} nascosti)
            </span>
          </h3>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500"/>
            <input type="text" placeholder="Cerca…" value={query} onChange={e => setQuery(e.target.value)}
              data-testid="admin-search-sponsor"
              className="pl-7 pr-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-xs text-white w-48"/>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
          Tutti gli sponsor sono <span className="text-emerald-300 font-bold">disponibili di default</span> nei caroselli. Usa il toggle per nasconderne uno ai manager (verrà escluso da entrambi i caroselli Bonus e Pagato).
        </p>
        <div className="grid gap-2" data-testid="admin-templates-list">
          {filtered.map(tpl => {
            const isHidden = hiddenIds.has(tpl.sponsor_id);
            const isBusy = !!busy[tpl.sponsor_id];
            return (
              <div key={tpl.sponsor_id}
                data-testid={`admin-tpl-${tpl.sponsor_id}`}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2 transition-colors ${
                  isHidden ? "border-slate-800 bg-slate-950/40 opacity-60" : "border-slate-800 bg-slate-900/60 hover:border-slate-600"
                }`}>
                {tpl.logo_url && (
                  <img src={tpl.logo_url} alt={tpl.name}
                    className="w-11 h-11 object-contain shrink-0"
                    style={{ filter: isHidden ? "grayscale(1)" : "drop-shadow(0 2px 6px rgba(0,0,0,0.4))" }}/>
                )}
                <label className="cursor-pointer inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:text-emerald-300 shrink-0" title="Cambia logo (superadmin richiesto per template piattaforma)">
                  <input type="file" accept="image/*" onChange={changeLogo(tpl)} className="hidden"
                    data-testid={`admin-change-logo-tpl-${tpl.sponsor_id}`}/>
                  {busy[`logo_${tpl.sponsor_id}`] ? <Loader2 className="animate-spin" size={11}/> : "↺"}
                </label>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-black text-white truncate">{tpl.name}</span>
                    {isHidden && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-widest bg-rose-500/20 text-rose-300 border border-rose-500/40">
                        Nascosto
                      </span>
                    )}
                  </div>
                  {tpl.tagline && <div className="text-[10px] italic text-slate-400 truncate">{tpl.tagline}</div>}
                  {tpl.objective_desc && <div className="text-[10px] text-slate-500 truncate mt-0.5">🎯 {tpl.objective_desc}</div>}
                </div>
                <div className="text-[10px] text-slate-500 font-mono uppercase tracking-widest shrink-0 text-right">
                  Base<br/><span className="text-emerald-300 font-black text-sm">{tpl.prize_credits}c</span>
                </div>
                <button onClick={() => toggleHidden(tpl)} disabled={isBusy}
                  data-testid={`admin-toggle-hidden-${tpl.sponsor_id}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-colors ${
                    isHidden
                      ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20"
                      : "bg-slate-800 border-slate-700 text-slate-300 hover:border-rose-500/40 hover:text-rose-300"
                  } disabled:opacity-30`}>
                  {isBusy ? <Loader2 className="animate-spin" size={11}/>
                    : isHidden ? <><Eye size={11}/> Mostra</>
                    : <><EyeOff size={11}/> Non mostrare</>}
                </button>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center text-slate-500 text-xs py-6">Nessuno sponsor corrisponde alla ricerca.</div>
          )}
        </div>
      </div>

      {createOpen && (
        <SponsorCreateDialog leagueId={leagueId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }}/>
      )}
    </div>
  );
}

function ToggleRow({ label, value, onClick }) {
  return (
    <button onClick={onClick} className={`flex items-center justify-between gap-2 p-2.5 rounded-xl border transition-colors ${
      value ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-950/50 border-slate-800"
    }`}>
      <span className="text-xs font-bold text-left">{label}</span>
      {value ? <ToggleRight size={22} className="text-emerald-400"/> : <ToggleLeft size={22} className="text-slate-500"/>}
    </button>
  );
}
